import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { Heart, Users, Presentation, Code, Sparkles } from 'lucide-react'
import ecosystem from '../data/ecosystem.json'

const iconMap = {
  Users,
  Presentation,
  Code,
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
}

export default function Volunteering() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const { volunteering } = ecosystem

  return (
    <section id="volunteering" className="relative py-20 overflow-hidden lg:py-32 bg-gradient-to-br from-primary-900 via-primary-800 to-rose-valley-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      
      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-rose-valley-500/20 blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-primary-500/20 blur-3xl" />

      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-white/10 text-white/90">
              <Heart className="w-4 h-4 mr-1.5 text-rose-valley-400" />
              Engagement
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {volunteering.title}
            </h2>
            <p className="mt-4 text-lg text-white/70">
              {volunteering.description}
            </p>
          </motion.div>

          {/* Activities Grid */}
          <motion.div
            variants={containerVariants}
            className="grid gap-6 mt-16 md:grid-cols-3"
          >
            {volunteering.activities.map((activity, index) => {
              const Icon = iconMap[activity.icon] || Users
              return (
                <motion.div
                  key={activity.type}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, y: -4 }}
                  className="relative p-8 overflow-hidden transition-all border group rounded-3xl bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 hover:border-white/20"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 translate-x-8 -translate-y-8 rounded-full bg-gradient-to-br from-rose-valley-500/20 to-primary-500/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative">
                    <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10">
                      <Icon className="w-7 h-7 text-rose-valley-400" />
                    </div>
                    
                    <h3 className="mt-6 text-xl font-bold text-white">{activity.type}</h3>
                    <p className="mt-3 text-white/60">{activity.description}</p>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>

          {/* CTA */}
          <motion.div
            variants={itemVariants}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white">
              <Sparkles className="w-5 h-5 text-rose-valley-400" />
              <span className="text-sm font-medium">
                Intéressé par une collaboration ? 
                <a
                  href="mailto:contact@abderrahi.me"
                  className="ml-2 underline underline-offset-4 hover:text-rose-valley-300 transition-colors"
                >
                  Contactez-moi
                </a>
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
